import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Collapse from '@mui/material/Collapse';
import Fade from '@mui/material/Fade';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import { Check, ChevronDown, LayoutGrid, ListChecks, Package, Pencil, Plus, Trash2, Wrench, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { httpClient } from '../../common/httpclient';
import MobileCategoryForm from '../../component/category/MobileCategoryForm';
import DialogForm from '../../component/dialog/DialogForm';
import { useAppBarTitle } from '../../component/layout/AppBarActions';
import PageScaffold from '../../component/pageScaffold/PageScaffold';
import { useIsMobileShell } from '../../hooks/useIsMobileShell';

// Radio-card de Tipo (Produto azul/info · Serviço roxo/secondary) —
// DESIGN-SYSTEM.md §5.5, mesmo padrão de `TypeCard` de
// `pages/suppliers/Form.js` (Pessoa Jurídica/Física), aqui com as cores do
// domínio `itemType` do `StatusChip` (produto=info, servico=secondary/roxo).
function TypeCard({ active, tone, icon: Icon, title, description, onClick, disabled }) {
    const theme = useTheme();
    const activeColor = tone === 'servico' ? theme.palette.secondary.main : theme.palette.primary.main;
    const activeBg = tone === 'servico' ? theme.palette.secondary.bg : theme.palette.info.bg;
    return (
        <Box
            onClick={disabled ? undefined : onClick}
            sx={{
                position: 'relative',
                border: '1.5px solid',
                borderColor: active ? activeColor : 'divider',
                borderRadius: '14px',
                p: '14px 16px',
                cursor: disabled ? 'default' : 'pointer',
                opacity: disabled && !active ? 0.55 : 1,
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                backgroundColor: active ? activeBg : 'background.subtle',
                transition: 'border-color .15s, background-color .15s',
                '&:hover': disabled ? {} : { borderColor: active ? activeColor : 'text.disabled' },
            }}
        >
            <Box
                sx={{
                    width: 34,
                    height: 34,
                    borderRadius: '9px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    backgroundColor: active ? activeColor : 'background.paper',
                    color: active ? '#fff' : 'text.disabled',
                    border: '1px solid',
                    borderColor: active ? activeColor : 'divider',
                    transition: 'background-color .15s, color .15s',
                }}
            >
                <Icon size={16} />
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 700, mb: '2px' }}>{title}</Typography>
                <Typography sx={{ fontSize: 11, color: 'text.disabled', lineHeight: 1.35 }}>{description}</Typography>
            </Box>
            {active && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 10,
                        right: 10,
                        width: 16,
                        height: 16,
                        borderRadius: '50%',
                        backgroundColor: activeColor,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <Check size={10} strokeWidth={3} />
                </Box>
            )}
        </Box>
    );
}

// Casca visual de campo "ff-input"/"ff-textarea" (52px/auto, radius 12px,
// fundo surface-2, label flutuante interno) — cópia 1:1 de
// `standaloneInputBoxSx`/`standaloneFieldSx` já usados em
// `suppliers/Form.js`/`services/Form.js`/`products/Form.js`.
function useStandaloneFieldSx() {
    const standaloneInputBoxSx = {
        '& .MuiOutlinedInput-root': {
            borderRadius: '12px',
            backgroundColor: 'background.subtle',
            height: '52px',
            transition: 'background-color .15s ease, box-shadow .15s ease',
            '&.MuiInputBase-multiline': {
                height: 'auto',
                minHeight: '96px',
                alignItems: 'flex-start',
                padding: '24px 14px 7px 14px',
            },
        },
        '& .MuiOutlinedInput-input:not(textarea):not(.MuiSelect-select)': {
            padding: '21px 14px 7px 14px',
        },
        '& .MuiOutlinedInput-notchedOutline': {
            borderWidth: '1.5px',
            borderColor: 'divider',
        },
        '& .MuiOutlinedInput-notchedOutline legend': {
            display: 'none',
        },
        '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: 'primary.main',
            borderWidth: '1.5px',
        },
        '& .MuiInputBase-multiline textarea': {
            resize: 'vertical',
        },
    };

    return {
        '& .MuiInputBase-input': { fontSize: '13.5px' },
        '& .MuiInputLabel-root': {
            fontSize: '13.5px',
            color: 'text.disabled',
            transform: 'translate(14px, 17px) scale(1)',
        },
        '& .MuiInputLabel-root.MuiInputLabel-shrink': {
            fontSize: '10px',
            fontWeight: 800,
            color: 'primary.main',
            letterSpacing: '.04em',
            textTransform: 'uppercase',
            transform: 'translate(14px, 8px) scale(1)',
        },
        ...standaloneInputBoxSx,
    };
}

function emptyDraft(type) {
    return { type, name: '', description: '', margin: '' };
}

// Formulário de categoria/subcategoria — sempre renderizado como conteúdo do
// drawer lateral aberto por `pages/categories/List.js`
// (`prototipo-lista-categorias.html`). Não existe protótipo de página cheia
// dedicado (ver FRONTEND-TASKS.md "Formulário de categoria") — quando esta
// rota é acessada diretamente (`/categories/new`, `/categories/edit/:id`,
// fora do drawer), ela apenas redireciona para `/categories`, onde o mesmo
// formulário roda embutido.
export default function Form({
    embedded = false,
    categoryId: categoryIdProp = null,
    categoryType: categoryTypeProp = 'produto',
    onClose: onCloseProp,
    onSuccess: onSuccessProp,
    onError: onErrorProp,
}) {
    const navigate = useNavigate();
    const theme = useTheme();
    const standaloneFieldSx = useStandaloneFieldSx();
    const routeParams = useParams();
    const [searchParams] = useSearchParams();
    const isMobileShell = useIsMobileShell();
    // Shell mobile: a rota abre o formulário em página cheia (`categorias/form.html`);
    // no desktop continua sendo o drawer da lista.
    const mobilePage = !embedded && isMobileShell;
    const categoryId = mobilePage ? (routeParams.id || null) : categoryIdProp;
    const categoryType = mobilePage ? (searchParams.get('type') === 'servico' ? 'servico' : 'produto') : categoryTypeProp;
    const [snack, setSnack] = useState({ open: false, message: '' });
    const onClose = mobilePage ? () => navigate('/categories') : onCloseProp;
    const onSuccess = mobilePage ? undefined : onSuccessProp;
    const onError = mobilePage
        ? (msg) => setSnack({ open: true, message: typeof msg === 'string' ? msg : 'Erro ao salvar categoria' })
        : onErrorProp;
    useAppBarTitle(mobilePage && categoryId ? 'Editar categoria' : '');

    // Sem protótipo de página cheia — acessar a rota diretamente devolve o
    // usuário para a lista, onde o drawer cobre o mesmo formulário.
    useEffect(() => {
        if (!embedded && !isMobileShell) {
            navigate('/categories', { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, isMobileShell]);

    const [id, setId] = useState(categoryId);
    const [type, setType] = useState(categoryType || 'produto');
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [margin, setMargin] = useState('');
    const [nameError, setNameError] = useState('');
    const [marginError, setMarginError] = useState('');

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);

    const [subcategories, setSubcategories] = useState([]);
    const [subSectionOpen, setSubSectionOpen] = useState(true);

    const [subDialogOpen, setSubDialogOpen] = useState(false);
    const [editingSub, setEditingSub] = useState(null);
    const [subName, setSubName] = useState('');
    const [subNameError, setSubNameError] = useState('');
    const [subMargin, setSubMargin] = useState('');
    const [subMarginError, setSubMarginError] = useState('');
    const [subSaving, setSubSaving] = useState(false);

    const [deleteSubTarget, setDeleteSubTarget] = useState(null);
    const [subDeleting, setSubDeleting] = useState(false);

    const isEdit = Boolean(id);
    const isProduto = type === 'produto';

    const resetForm = () => {
        const draft = emptyDraft(categoryType || 'produto');
        setType(draft.type);
        setName(draft.name);
        setDescription(draft.description);
        setMargin(draft.margin);
        setSubcategories([]);
        setNameError('');
        setMarginError('');
    };

    const fetchCategory = async (categoryIdToFetch, categoryTypeToFetch) => {
        setLoading(true);
        try {
            const endpoint = categoryTypeToFetch === 'produto'
                ? `/category/${categoryIdToFetch}`
                : `/category-service/${categoryIdToFetch}`;
            const response = await httpClient.get(endpoint);
            const category = response.data.category || response.data;
            setName(category.name || '');
            setDescription(category.description || '');
            setMargin(categoryTypeToFetch === 'produto' && category.profit_margin !== undefined && category.profit_margin !== null
                ? String(category.profit_margin)
                : '');
            const subs = response.data.sub_categories || response.data.subcategories || [];
            setSubcategories(subs.map((s) => ({
                id: s.id,
                name: s.name,
                // Descrição/margem não são editáveis pelo sub-dialog (ver
                // `prototipo-lista-categorias.html`, que só coleta o nome da
                // subcategoria) — preservados aqui apenas para reenviar sem
                // alteração num PUT, evitando apagar um valor previamente
                // cadastrado pelo formulário antigo.
                description: s.description || '',
                profit_margin: s.profit_margin !== undefined ? s.profit_margin : null,
            })));
        } catch (error) {
            console.error('Erro ao carregar categoria:', error);
            onError?.(error.response?.data || error.message || 'Erro ao carregar categoria');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        setId(categoryId);
        if (categoryId) {
            setType(categoryType || 'produto');
            fetchCategory(categoryId, categoryType || 'produto');
        } else {
            resetForm();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, categoryType]);

    const handleSelectType = (nextType) => {
        if (isEdit) return; // tipo não pode mudar após a categoria existir (tabelas diferentes no backend)
        setType(nextType);
        if (nextType !== 'produto') {
            setMargin('');
            setMarginError('');
        }
    };

    const handleSaveCategory = async () => {
        const trimmedName = name.trim();
        if (!trimmedName) {
            setNameError('Informe o nome da categoria.');
            return;
        }
        setNameError('');

        let marginValue = null;
        if (isProduto && margin !== '') {
            const parsed = parseFloat(String(margin).replace(',', '.'));
            if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
                setMarginError('A margem deve ser entre 0 e 100.');
                return;
            }
            marginValue = parsed;
        }
        setMarginError('');

        setSaving(true);
        try {
            const payload = isProduto
                ? { name: trimmedName, description: description.trim(), profit_margin: marginValue ?? 0 }
                : { name: trimmedName, description: description.trim() };

            if (isEdit) {
                const endpoint = isProduto ? `/category/${id}` : `/category-service/${id}`;
                await httpClient.put(endpoint, payload);
                onSuccess?.();
            } else {
                const endpoint = isProduto ? '/category' : '/category-service';
                await httpClient.post(endpoint, payload);
                onSuccess?.();
            }
            onClose?.();
        } catch (error) {
            console.error('Erro ao salvar categoria:', error);
            onError?.(error.response?.data || error.message || 'Erro ao salvar categoria');
        } finally {
            setSaving(false);
        }
    };

    const openNewSubDialog = () => {
        setEditingSub(null);
        setSubName('');
        setSubNameError('');
        setSubMargin('');
        setSubMarginError('');
        setSubDialogOpen(true);
    };

    const openEditSubDialog = (sub) => {
        setEditingSub(sub);
        setSubName(sub.name);
        setSubNameError('');
        setSubMargin(sub.profit_margin !== undefined && sub.profit_margin !== null ? String(sub.profit_margin) : '');
        setSubMarginError('');
        setSubDialogOpen(true);
    };

    const closeSubDialog = () => {
        setSubDialogOpen(false);
        setEditingSub(null);
    };

    const handleSaveSubcategory = async () => {
        const trimmed = subName.trim();
        if (!trimmed) {
            setSubNameError('Informe o nome da subcategoria.');
            return;
        }
        setSubNameError('');

        let subMarginValue = null;
        if (isProduto && subMargin !== '') {
            const parsed = parseFloat(String(subMargin).replace(',', '.'));
            if (Number.isNaN(parsed) || parsed < 0 || parsed > 100) {
                setSubMarginError('A margem deve ser entre 0 e 100.');
                return;
            }
            subMarginValue = parsed;
        }
        setSubMarginError('');

        setSubSaving(true);
        try {
            if (editingSub) {
                const endpoint = isProduto
                    ? `/category/subcategory/${editingSub.id}`
                    : `/category-service/subcategory/${editingSub.id}`;
                const payload = isProduto
                    ? { name: trimmed, description: editingSub.description || '', category_id: id, profit_margin: subMarginValue }
                    : { name: trimmed, description: editingSub.description || '', category_id: id };
                await httpClient.put(endpoint, payload);
            } else {
                const endpoint = isProduto ? '/category/subcategory' : '/category-service/subcategory';
                const payload = isProduto
                    ? { name: trimmed, description: '', category_id: id, profit_margin: subMarginValue }
                    : { name: trimmed, description: '', category_id: id };
                await httpClient.post(endpoint, payload);
            }
            closeSubDialog();
            await fetchCategory(id, type);
            onSuccess?.({ keepOpen: true });
        } catch (error) {
            console.error('Erro ao salvar subcategoria:', error);
            onError?.(error.response?.data || error.message || 'Erro ao salvar subcategoria');
        } finally {
            setSubSaving(false);
        }
    };

    const handleDeleteSubcategory = async () => {
        if (!deleteSubTarget) return;
        setSubDeleting(true);
        try {
            const endpoint = isProduto
                ? `/category/subcategory/${deleteSubTarget.id}`
                : `/category-service/subcategory/${deleteSubTarget.id}`;
            await httpClient.delete(endpoint);
            setDeleteSubTarget(null);
            await fetchCategory(id, type);
            onSuccess?.({ keepOpen: true });
        } catch (error) {
            console.error('Erro ao excluir subcategoria:', error);
            onError?.(error.response?.data || error.message || 'Erro ao excluir subcategoria');
        } finally {
            setSubDeleting(false);
        }
    };

    if (mobilePage) {
        const openSub = (sub) => (sub ? openEditSubDialog(sub) : openNewSubDialog());
        return (
            <PageScaffold
                links={[]}
                openSnackbar={snack.open}
                setOpenSnackbar={(open) => setSnack((prev) => ({ ...prev, open }))}
                message={snack.message}
                severity="error"
            >
                <MobileCategoryForm
                    isEdit={isEdit}
                    isProduto={isProduto}
                    onTypeChange={handleSelectType}
                    name={name}
                    onNameChange={(value) => { setName(value); if (nameError) setNameError(''); }}
                    nameError={nameError}
                    description={description}
                    onDescriptionChange={setDescription}
                    margin={margin}
                    onMarginChange={(value) => { setMargin(value); if (marginError) setMarginError(''); }}
                    marginError={marginError}
                    subcategories={subcategories}
                    onNewSub={() => openSub(null)}
                    onEditSub={openSub}
                    sub={{
                        open: subDialogOpen,
                        editing: Boolean(editingSub),
                        onClose: closeSubDialog,
                        onSave: handleSaveSubcategory,
                        saving: subSaving,
                        name: subName,
                        onNameChange: (value) => { setSubName(value); if (subNameError) setSubNameError(''); },
                        nameError: subNameError,
                        margin: subMargin,
                        onMarginChange: (value) => { setSubMargin(value); if (subMarginError) setSubMarginError(''); },
                        marginError: subMarginError,
                        onDelete: () => {
                            const target = editingSub;
                            closeSubDialog();
                            setDeleteSubTarget(target);
                        },
                    }}
                    onCancel={() => onClose?.()}
                    onSave={handleSaveCategory}
                    saving={saving}
                    loading={loading}
                />
                <DialogForm
                    open={Boolean(deleteSubTarget)}
                    iconType="warning"
                    onClose={() => setDeleteSubTarget(null)}
                    onSubmit={handleDeleteSubcategory}
                    title="Excluir Subcategoria"
                    content={`Tem certeza que deseja excluir a subcategoria "${deleteSubTarget?.name || ''}"?`}
                    secondaryContent="Esta ação não pode ser desfeita."
                    confirmButtonText="Excluir"
                    cancelButtonText="Cancelar"
                    loading={subDeleting}
                    loadingText="Excluindo..."
                />
            </PageScaffold>
        );
    }

    if (!embedded) {
        return null;
    }

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', backgroundColor: 'background.default' }}>
            {/* drawer-head — ícone-badge + título + subtítulo + X. */}
            <Box sx={{ p: '22px 22px 16px', borderBottom: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.5, flexShrink: 0 }}>
                <Box sx={{ minWidth: 0 }}>
                    <Box sx={{
                        width: 38,
                        height: 38,
                        borderRadius: '11px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        mb: '10px',
                        backgroundColor: 'info.bg',
                        color: 'primary.main',
                    }}>
                        <LayoutGrid size={18} strokeWidth={2} />
                    </Box>
                    <Typography sx={{ fontSize: 17, fontWeight: 800, mb: '4px' }}>
                        {isEdit ? 'Editar categoria' : 'Nova categoria'}
                    </Typography>
                    <Typography sx={{ fontSize: '12.5px', color: 'text.secondary' }}>
                        {isEdit ? `Atualize os dados da categoria "${name || ''}".` : 'Cadastre uma nova categoria de produto ou serviço.'}
                    </Typography>
                </Box>
                <IconButton onClick={() => onClose?.()} size="small" aria-label="Fechar">
                    <X size={18} strokeWidth={2} />
                </IconButton>
            </Box>

            <Box sx={{ p: '20px 22px', flex: 1, overflowY: 'auto' }}>
                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 260, flexDirection: 'column' }}>
                        <CircularProgress size={40} thickness={4} />
                        <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                            Carregando categoria...
                        </Typography>
                    </Box>
                ) : (
                    <Fade in={!loading} timeout={300}>
                        <Box>
                            <Typography sx={{ fontSize: '10.5px', fontWeight: 800, color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '.05em', mb: '10px' }}>
                                Tipo <Box component="span" sx={{ color: 'error.main' }}>*</Box>
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', mb: '20px' }}>
                                <TypeCard
                                    active={isProduto}
                                    tone="produto"
                                    icon={Package}
                                    title="Produto"
                                    description="Categoriza itens do catálogo de produtos, com margem de lucro própria."
                                    onClick={() => handleSelectType('produto')}
                                    disabled={isEdit}
                                />
                                <TypeCard
                                    active={!isProduto}
                                    tone="servico"
                                    icon={Wrench}
                                    title="Serviço"
                                    description="Categoriza serviços oferecidos no catálogo de ordens de serviço."
                                    onClick={() => handleSelectType('servico')}
                                    disabled={isEdit}
                                />
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    label="Nome da categoria"
                                    value={name}
                                    onChange={(e) => { setName(e.target.value); if (nameError) setNameError(''); }}
                                    error={Boolean(nameError)}
                                    helperText={nameError}
                                    variant="outlined"
                                    inputProps={{ maxLength: 255 }}
                                    sx={standaloneFieldSx}
                                />
                            </Box>

                            {isProduto ? (
                                <Box sx={{ mb: 2 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label="Margem de lucro (%)"
                                        value={margin}
                                        onChange={(e) => { setMargin(e.target.value); if (marginError) setMarginError(''); }}
                                        error={Boolean(marginError)}
                                        helperText={marginError}
                                        variant="outlined"
                                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                                        sx={standaloneFieldSx}
                                    />
                                </Box>
                            ) : (
                                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: '-8px', mb: 2 }}>
                                    Margem de lucro se aplica somente a categorias do tipo Produto.
                                </Typography>
                            )}

                            <Box sx={{ mb: 2.5 }}>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="Descrição (opcional)"
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    variant="outlined"
                                    inputProps={{ maxLength: 255 }}
                                    sx={standaloneFieldSx}
                                />
                            </Box>

                            {/* acc-section — subcategorias, com tabela aninhada + sub-dialog. */}
                            <Box sx={{
                                backgroundColor: 'background.subtle',
                                border: '1.5px solid',
                                borderColor: subSectionOpen ? 'primary.main' : 'divider',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                boxShadow: subSectionOpen ? `0 0 0 3px ${theme.palette.info.bg}` : 'none',
                                transition: 'border-color .2s, box-shadow .2s',
                            }}>
                                <Box
                                    onClick={() => setSubSectionOpen((v) => !v)}
                                    sx={{ display: 'flex', alignItems: 'center', gap: '12px', p: '14px 16px', cursor: 'pointer' }}
                                >
                                    <Box sx={{
                                        width: 34,
                                        height: 34,
                                        borderRadius: '10px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexShrink: 0,
                                        backgroundColor: 'background.paper',
                                        color: 'primary.main',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                    }}>
                                        <ListChecks size={16} strokeWidth={2} />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography sx={{ fontSize: '13.5px', fontWeight: 700 }}>Subcategorias</Typography>
                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled' }}>
                                            {subcategories.length} subcategoria{subcategories.length === 1 ? '' : 's'} cadastrada{subcategories.length === 1 ? '' : 's'}
                                        </Typography>
                                    </Box>
                                    <ChevronDown
                                        size={16}
                                        color={theme.palette.text.disabled}
                                        style={{ transition: 'transform .2s', transform: subSectionOpen ? 'rotate(180deg)' : 'none', flexShrink: 0 }}
                                    />
                                </Box>
                                <Collapse in={subSectionOpen}>
                                    <Box sx={{ p: '0 16px 16px' }}>
                                        {!isEdit ? (
                                            <Typography sx={{ fontSize: 12, color: 'text.disabled', fontStyle: 'italic', py: 1 }}>
                                                Salve a categoria para cadastrar subcategorias.
                                            </Typography>
                                        ) : (
                                            <>
                                                {subcategories.length === 0 ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: '26px', px: '10px', gap: '6px', textAlign: 'center' }}>
                                                        <Box sx={{ width: 44, height: 44, borderRadius: '50%', border: '2px dashed', borderColor: 'divider', color: 'text.disabled', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                            <ListChecks size={20} strokeWidth={1.6} />
                                                        </Box>
                                                        <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'text.primary' }}>Nenhuma subcategoria</Typography>
                                                        <Typography sx={{ fontSize: '11.5px', color: 'text.disabled', maxWidth: 260 }}>
                                                            Adicione subcategorias para refinar a organização deste grupo.
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: '10px', overflow: 'hidden', backgroundColor: 'background.paper' }}>
                                                        {subcategories.map((sub, index) => (
                                                            <Box
                                                                key={sub.id}
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'space-between',
                                                                    gap: 1,
                                                                    p: '9px 12px',
                                                                    borderBottom: index < subcategories.length - 1 ? '1px solid' : 'none',
                                                                    borderColor: 'divider',
                                                                }}
                                                            >
                                                                <Typography sx={{ fontSize: '12.5px', fontWeight: 700, color: 'text.primary' }} noWrap>
                                                                    {sub.name}
                                                                </Typography>
                                                                <Box sx={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                                                                    <IconButton size="small" onClick={() => openEditSubDialog(sub)} sx={{ width: 24, height: 24, borderRadius: '8px', border: '1px solid', borderColor: 'divider' }}>
                                                                        <Pencil size={12} strokeWidth={2} />
                                                                    </IconButton>
                                                                    <IconButton size="small" onClick={() => setDeleteSubTarget(sub)} sx={{ width: 24, height: 24, borderRadius: '8px', border: '1px solid', borderColor: 'divider', color: 'error.main' }}>
                                                                        <Trash2 size={12} strokeWidth={2} />
                                                                    </IconButton>
                                                                </Box>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                )}
                                                <Button
                                                    fullWidth
                                                    variant="outlined"
                                                    size="small"
                                                    startIcon={<Plus size={13} strokeWidth={2} />}
                                                    onClick={openNewSubDialog}
                                                    sx={{ mt: '10px' }}
                                                >
                                                    Nova subcategoria
                                                </Button>
                                            </>
                                        )}
                                    </Box>
                                </Collapse>
                            </Box>
                        </Box>
                    </Fade>
                )}
            </Box>

            <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider', display: 'flex', justifyContent: 'flex-end', gap: 1.5, flexShrink: 0 }}>
                <Button variant="outlined" onClick={() => onClose?.()} size="small" disabled={saving}>
                    Cancelar
                </Button>
                <Button variant="contained" onClick={handleSaveCategory} size="small" disabled={saving || loading}>
                    {saving ? 'Salvando...' : 'Salvar categoria'}
                </Button>
            </Box>

            {/* sub-dialog: add/edit subcategoria — nome + margem de lucro
                (produto), mesmo padrão de `prototipo-lista-categorias.html`
                (modal-box narrow). */}
            <DialogForm
                open={subDialogOpen}
                iconType="info"
                onClose={closeSubDialog}
                onSubmit={handleSaveSubcategory}
                title={editingSub ? 'Editar subcategoria' : 'Nova subcategoria'}
                subtitle="Informe o nome da subcategoria."
                confirmButtonText="Salvar"
                cancelButtonText="Cancelar"
                loading={subSaving}
                loadingText="Salvando..."
            >
                <Box sx={{ mb: isProduto ? 2 : 0 }}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Nome da subcategoria"
                        value={subName}
                        onChange={(e) => { setSubName(e.target.value); if (subNameError) setSubNameError(''); }}
                        error={Boolean(subNameError)}
                        helperText={subNameError}
                        variant="outlined"
                        inputProps={{ maxLength: 255 }}
                        sx={standaloneFieldSx}
                    />
                </Box>
                {isProduto && (
                    <TextField
                        fullWidth
                        type="number"
                        label="Margem de lucro (%)"
                        value={subMargin}
                        onChange={(e) => { setSubMargin(e.target.value); if (subMarginError) setSubMarginError(''); }}
                        error={Boolean(subMarginError)}
                        helperText={subMarginError}
                        variant="outlined"
                        inputProps={{ min: 0, max: 100, step: 0.5 }}
                        sx={standaloneFieldSx}
                    />
                )}
            </DialogForm>

            {/* Confirmação de exclusão de subcategoria — o protótipo (demo
                sem backend) remove direto ao clicar, mas aqui a exclusão
                dispara uma chamada real a
                `DELETE /category(-service)/subcategory/:id` (soft delete no
                backend, some da listagem imediatamente); mantido o modal de
                confirmação já padrão em todo o app para qualquer ação
                destrutiva (DESIGN-SYSTEM.md), preservando também o
                comportamento do formulário antigo. */}
            <DialogForm
                open={Boolean(deleteSubTarget)}
                iconType="warning"
                onClose={() => setDeleteSubTarget(null)}
                onSubmit={handleDeleteSubcategory}
                title="Excluir Subcategoria"
                content={`Tem certeza que deseja excluir a subcategoria "${deleteSubTarget?.name || ''}"?`}
                secondaryContent="Esta ação não pode ser desfeita."
                confirmButtonText="Excluir"
                cancelButtonText="Cancelar"
                loading={subDeleting}
                loadingText="Excluindo..."
            />
        </Box>
    );
}
